import { IT_INVENTORY_MDM_STATE, IT_INVENTORY_PROFILE_COMPLIANCE } from '@/lib/constants/itInventory'
import { HARDWARE_ASSET_STATUSES, HARDWARE_ASSET_TYPES } from '@/lib/constants/hardwareAssets'
import { CLOUD_RESOURCE_DETAILS, CLOUD_RESOURCE_TYPES } from '@/lib/constants/cloudResources'

export const IT_INVENTORY = {
  TITLE: 'Inventario TI',
  SEARCH_PLACEHOLDER: 'Buscar por hostname o responsable',
  TABS: {
    LAPTOPS: 'Portátiles',
    MOBILES: 'Móviles',
    SERVERS: 'Servidores',
    CLOUD: 'Cloud',
    CODE: 'Código',
    HARDWARE: 'Hardware',
  },
  SUMMARY: {
    AGENTS_DEPLOYED: 'agentes desplegados',
    COVERED: 'Cubiertos',
    NOT_COVERED: 'Sin cubrir',
    VIEW_EMPLOYEES: 'Ver empleados',
    COVERAGE: 'Cobertura',
  },
  TABLE: {
    ENDPOINT: 'Endpoint',
    UPDATES: 'Actualizaciones',
    OS: 'SO',
    AGENT: 'Agente',
    MDM: 'MDM',
    ASSIGNED_TO: 'Asignado a',
    LAST_SEEN: 'Última conexión',
    PROFILE_COMPLIANCE: 'Cumplimiento del perfil',
    HARDWARE: 'Hardware',
    ACCOUNT: 'Cuenta',
    ACCOUNT_ID: 'ID de cuenta',
    PROVIDER: 'Proveedor',
    REGIONS: 'Regiones',
    REGIONS_COUNT: (count: number) => `${count} regiones`,
    REPOSITORY: 'Repositorio',
    SCM: 'SCM',
    PROJECT_KEY: 'Clave de proyecto',
    CLONE_URL: 'URL de clonado',
    ADDED: 'Añadido',
    ITEM: 'Elemento',
    TYPE: 'Tipo',
    SERIAL: 'Serie',
    LOCATION: 'Ubicación',
    STATUS: 'Estado',
    NOTES: 'Notas',
    EMPTY: '-',
    UNASSIGNED: 'Sin asignar',
    STORAGE_VALUE: (totalGb: number) => `${totalGb} GB`,
    RESOURCE: 'Recurso',
    OWNER: 'Responsable',
    USER_DATA: 'Datos de usuario',
    NEEDS_OWNER: 'Sin responsable',
    YES: 'Sí',
    NO: 'No',
  },
  DRAWER: {
    HARDWARE: 'Hardware',
    SYSTEM_DETAILS: 'Detalles del sistema',
    ARCHITECTURE: 'Arquitectura',
    SERIAL_NUMBER: 'Número de serie',
    EDR_AGENT: 'Agente EDR',
    EDR_AGENT_VALUE: (agentName: string, version?: string) => (version ? `${agentName} · ${version}` : agentName),
    MODEL: 'Modelo',
    STORAGE: 'Almacenamiento',
    NEVER_SEEN: '-',
    QUICK_ACTIONS: 'Acciones rápidas',
  },
  HARDWARE_TYPES: {
    [HARDWARE_ASSET_TYPES.MONITOR]: 'Monitor',
    [HARDWARE_ASSET_TYPES.PRINTER]: 'Impresora',
    [HARDWARE_ASSET_TYPES.MOUSE]: 'Ratón',
    [HARDWARE_ASSET_TYPES.DOCK]: 'Dock',
    [HARDWARE_ASSET_TYPES.USB_DRIVE]: 'Memoria USB',
    [HARDWARE_ASSET_TYPES.SECURITY_KEY]: 'Llave de seguridad',
    [HARDWARE_ASSET_TYPES.OTHER]: 'Otro',
  },
  HARDWARE_STATUS: {
    [HARDWARE_ASSET_STATUSES.IN_USE]: 'En uso',
    [HARDWARE_ASSET_STATUSES.IN_STOCK]: 'En stock',
    [HARDWARE_ASSET_STATUSES.RETIRED]: 'Retirado',
  },
  HARDWARE_FORM: {
    NEW_TITLE: 'Nuevo hardware',
    NEW_ASSET: 'Nuevo activo',
    BULK_UPLOAD: 'Carga masiva',
    ITEM_PLACEHOLDER: 'p. ej. Dell U2723QE 27"',
    SERIAL_PLACEHOLDER: 'p. ej. CN-0T2K9J-1',
    LOCATION_PLACEHOLDER: 'p. ej. Oficina de Madrid',
    NOTES_PLACEHOLDER: 'Cualquier detalle que merezca la pena registrar',
    SELECT_TYPE: 'Selecciona un tipo',
    SEARCH_TYPES: 'Buscar tipos',
    NO_TYPE_FOUND: 'Ningún tipo encontrado',
    SELECT_STATUS: 'Selecciona un estado',
    SEARCH_STATUSES: 'Buscar estados',
    NO_STATUS_FOUND: 'Ningún estado encontrado',
    CREATE: 'Crear',
    CANCEL: 'Cancelar',
    INCOMPLETE: 'Elemento, tipo, número de serie y ubicación son obligatorios',
    CREATED: 'Hardware añadido',
    UPDATE_ERROR: 'No se pudo guardar el cambio',
    DELETE_ERROR: 'No se pudo eliminar el hardware',
    DELETE_SUCCESS: 'Hardware eliminado',
    DELETE_DIALOG: {
      TITLE: 'Eliminar hardware',
      QUESTION: '¿Seguro que quieres eliminar',
      CANCEL: 'Cancelar',
      DELETE: 'Eliminar',
    },
    NO_NOTES: 'Sin notas',
  },
  HARDWARE_BULK_UPLOAD: {
    TITLE: 'Importar hardware desde CSV',
    DESCRIPTION:
      'Sube un archivo CSV para importar hardware de forma masiva. Todas las filas deben ser válidas: si alguna tiene errores, no se crea nada y los errores se muestran a continuación.',
    UPLOAD_FILE: 'Subir CSV',
    UPLOADING: 'Subiendo...',
    DOWNLOAD_SAMPLE: 'Descargar CSV de ejemplo',
    CANCEL: 'Cancelar',
    SUCCESS: 'Hardware importado correctamente',
    VALIDATION_TABLE_ROW: 'Fila',
    VALIDATION_TABLE_FIELD: 'Campo',
    VALIDATION_TABLE_ERROR: 'Error',
    VALIDATION_TABLE_RECEIVED_VALUE: 'Valor recibido',
    HEADER_ERROR_TITLE: 'La cabecera de tu CSV tiene problemas',
    HEADER_ERROR_DESCRIPTION:
      'Corrige la fila de cabecera en tu archivo y vuelve a subirlo. Las columnas aceptadas se muestran a continuación.',
    ACCEPTED_COLUMNS_REQUIRED: 'Columnas obligatorias',
    ACCEPTED_COLUMNS_OPTIONAL: 'Columnas opcionales',
    SUMMARY_LINE:
      '{totalRows} filas revisadas, {errorCount} errores encontrados. Corrígelos en tu CSV y vuelve a subirlo.',
    ERROR_UNKNOWN_COLUMN: 'Columna desconocida: {field}',
    ERROR_MISSING_REQUIRED_COLUMN: 'Falta la columna obligatoria: {field}',
    ERROR_EMPTY_FILE: 'El archivo tiene cabecera pero ninguna fila de hardware',
    ERROR_MALFORMED_ROW: 'El número de valores de esta fila no coincide con la cabecera',
    ERROR_REQUIRED: '{field} es obligatorio',
    ERROR_TOO_LONG: '{field} no puede superar los 255 caracteres',
    ERROR_INVALID_TYPE: 'Tipo no válido "{receivedValue}". Valores permitidos: {allowedValues}',
    ERROR_INVALID_STATUS: 'Estado no válido "{receivedValue}". Valores permitidos: {allowedValues}',
    ERROR_USER_NOT_FOUND: 'Usuario no encontrado en esta organización: "{receivedValue}"',
    ERROR_DUPLICATE_IN_FILE: 'Número de serie duplicado en este archivo: "{receivedValue}"',
    ERROR_DUPLICATE_IN_DB: 'Ya existe hardware con este número de serie: "{receivedValue}"',
  },
  CLOUD_FILTERS: {
    FILTER_BY_TYPE: 'Filtrar por tipo',
    FILTER_BY_PROVIDER: 'Filtrar por proveedor',
    FILTER_BY_ACCOUNT: 'Filtrar por cuenta',
    FILTER_BY_OWNER: 'Filtrar por responsable',
    FILTER_BY_USER_DATA: 'Filtrar por datos de usuario',
  },
  CLOUD_DRAWER: {
    OWNERSHIP: 'Responsabilidad',
    DESCRIPTION: 'Descripción',
    NO_DESCRIPTION: 'Sin descripción',
    CONTAINS_USER_DATA: 'Contiene datos de usuario',
    CONTAINS_USER_DATA_INFO: 'Almacena o procesa datos personales de clientes o empleados',
    DETAILS: 'Detalles',
    RESOURCE_ID: 'ID del recurso',
    SOURCE: 'Origen',
    SUBTITLE: (type: string, service: string) => `${type} · ${service}`,
    LAST_READ: (when: string) => `Última lectura ${when}`,
    TAGS: 'Etiquetas',
    NO_TAGS: 'Sin etiquetas',
    UPDATE_ERROR: 'No se pudo guardar el cambio',
  },
  CLOUD_RESOURCE_TYPES: {
    [CLOUD_RESOURCE_TYPES.ACCOUNT]: 'Cuenta cloud',
    [CLOUD_RESOURCE_TYPES.KUBERNETES]: 'Clúster de Kubernetes',
    [CLOUD_RESOURCE_TYPES.COMPUTE]: 'Instancia de cómputo',
    [CLOUD_RESOURCE_TYPES.BUCKET]: 'Bucket de almacenamiento',
    [CLOUD_RESOURCE_TYPES.DATABASE]: 'Base de datos',
    [CLOUD_RESOURCE_TYPES.FUNCTION]: 'Función',
    [CLOUD_RESOURCE_TYPES.LOAD_BALANCER]: 'Balanceador de carga',
    [CLOUD_RESOURCE_TYPES.REGISTRY]: 'Registro de contenedores',
    [CLOUD_RESOURCE_TYPES.KEY]: 'Clave de cifrado',
  },
  CLOUD_RESOURCE_DETAILS: {
    [CLOUD_RESOURCE_DETAILS.ENGINE]: 'Motor',
    [CLOUD_RESOURCE_DETAILS.KUBERNETES_VERSION]: 'Versión de Kubernetes',
    [CLOUD_RESOURCE_DETAILS.RUNTIME]: 'Runtime',
    [CLOUD_RESOURCE_DETAILS.INSTANCE_TYPE]: 'Tipo de instancia',
    [CLOUD_RESOURCE_DETAILS.IMAGE]: 'Imagen',
    [CLOUD_RESOURCE_DETAILS.NODES]: 'Nodos',
    [CLOUD_RESOURCE_DETAILS.MEMORY_MB]: 'Memoria (MB)',
    [CLOUD_RESOURCE_DETAILS.SIZE]: 'Tamaño',
    [CLOUD_RESOURCE_DETAILS.IMAGES]: 'Imágenes',
    [CLOUD_RESOURCE_DETAILS.KEY_SPEC]: 'Tipo de clave',
    [CLOUD_RESOURCE_DETAILS.ENCRYPTION]: 'Cifrado',
    [CLOUD_RESOURCE_DETAILS.ROTATION_DAYS]: 'Rotación (días)',
    [CLOUD_RESOURCE_DETAILS.VERSIONING]: 'Versionado',
    [CLOUD_RESOURCE_DETAILS.BACKUP_RETENTION_DAYS]: 'Retención de copias (días)',
    [CLOUD_RESOURCE_DETAILS.VULNERABILITY_SCANNING]: 'Escaneo de vulnerabilidades',
    [CLOUD_RESOURCE_DETAILS.PUBLIC_ENDPOINT]: 'Endpoint público',
    [CLOUD_RESOURCE_DETAILS.PUBLIC_ACCESS]: 'Acceso público',
    [CLOUD_RESOURCE_DETAILS.PUBLIC_IP]: 'IP pública',
    [CLOUD_RESOURCE_DETAILS.TLS_POLICY]: 'Política TLS',
    [CLOUD_RESOURCE_DETAILS.WAF]: 'WAF',
  },
  MDM_STATE: {
    [IT_INVENTORY_MDM_STATE.ONLINE]: 'Inscrito en MDM, en línea',
    [IT_INVENTORY_MDM_STATE.OFFLINE]: 'Inscrito en MDM, desconectado',
    [IT_INVENTORY_MDM_STATE.UNMANAGED]: 'No inscrito en MDM',
    [IT_INVENTORY_MDM_STATE.NO_AGENT]: 'Inscrito en MDM, pero el agente MDM no está instalado',
    [IT_INVENTORY_MDM_STATE.UNAVAILABLE]: 'MDM no responde, no se ha podido comprobar la inscripción',
  },
  MDM_STATE_SHORT: {
    [IT_INVENTORY_MDM_STATE.ONLINE]: 'En línea',
    [IT_INVENTORY_MDM_STATE.OFFLINE]: 'Desconectado',
    [IT_INVENTORY_MDM_STATE.UNMANAGED]: 'No inscrito',
    [IT_INVENTORY_MDM_STATE.NO_AGENT]: 'Agente MDM no instalado',
    [IT_INVENTORY_MDM_STATE.UNAVAILABLE]: 'MDM no disponible',
  },
  PROFILE_COMPLIANCE_STATE: {
    [IT_INVENTORY_PROFILE_COMPLIANCE.PASS]: 'Cumple',
    [IT_INVENTORY_PROFILE_COMPLIANCE.FAIL]: 'No cumple',
    [IT_INVENTORY_PROFILE_COMPLIANCE.NOT_APPLICABLE]: 'N/D',
  },
}
